import { useCallback, useMemo } from 'react';

import type {
  ApplyPlanModificationsRequestTModificationsItem,
  JsonValue,
  ProposedActionPayloadT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { buildRevisionToolOutput, isRevisionFlow, type SourceProposedActionT } from '@/shared/lib';
import { createProposedActionsApi } from '@/shared/api/lotus';

export interface ProposedActionMutations {
  approve: (externalId: string) => Promise<void>;
  reject: (externalId: string, reason: string) => Promise<void>;
  execute: (externalId: string, toolOutput?: Record<string, JsonValue> | null) => Promise<void>;
  rejectWithRevision: (
    externalId: string,
    toolOutput?: Record<string, JsonValue> | null
  ) => Promise<void>;
  finalizeFromSource: (
    source: SourceProposedActionT,
    toolOutput?: Record<string, JsonValue> | null
  ) => Promise<void>;
  switchAlternative: (externalId: string, alternativePayloadId: string) => Promise<void>;
  updatePayload: (externalId: string, payload: ProposedActionPayloadT) => Promise<void>;
}

function buildDecisionModification(
  externalId: string,
  decision: 'Approved' | 'Rejected'
): ApplyPlanModificationsRequestTModificationsItem {
  return {
    type: 'update',
    external_id: externalId,
    fields: { decision },
  };
}

export function useProposedActionMutations(): ProposedActionMutations {
  const { patientUserId } = useCareStateDomain();
  const api = useMemo(() => createProposedActionsApi(), []);

  const approve = useCallback(
    async (externalId: string): Promise<void> => {
      const result = await api.applyModifications(patientUserId, [
        buildDecisionModification(externalId, 'Approved'),
      ]);
      const firstError = result.errors[0];
      if (firstError) {
        throw new Error(firstError.message);
      }
    },
    [api, patientUserId]
  );

  const reject = useCallback(
    async (externalId: string, reason: string): Promise<void> => {
      const trimmedReason = reason.trim();
      await api.applyModifications(patientUserId, [
        {
          type: 'update',
          external_id: externalId,
          fields: {
            decision: 'Rejected',
            justification: `Rejected by clinician: ${trimmedReason}`,
          },
        },
      ]);
    },
    [api, patientUserId]
  );

  const execute = useCallback(
    async (externalId: string, toolOutput?: Record<string, JsonValue> | null): Promise<void> => {
      const result = await api.markExecuted(patientUserId, externalId, toolOutput);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to mark proposed action as executed');
      }
    },
    [api, patientUserId]
  );

  const rejectWithRevision = useCallback(
    async (externalId: string, toolOutput?: Record<string, JsonValue> | null): Promise<void> => {
      const result = await api.rejectWithRevision(patientUserId, externalId, toolOutput);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to reject proposed action with revision');
      }
    },
    [api, patientUserId]
  );

  const switchAlternative = useCallback(
    async (externalId: string, alternativePayloadId: string): Promise<void> => {
      await api.switchAlternative(patientUserId, externalId, alternativePayloadId);
    },
    [api, patientUserId]
  );

  const updatePayload = useCallback(
    async (externalId: string, payload: ProposedActionPayloadT): Promise<void> => {
      const result = await api.applyModifications(patientUserId, [
        { type: 'update', external_id: externalId, fields: { payload } },
      ]);
      const firstError = result.errors[0];
      if (firstError) {
        throw new Error(firstError.message);
      }
    },
    [api, patientUserId]
  );

  const finalizeFromSource = useCallback(
    async (
      source: SourceProposedActionT,
      toolOutput?: Record<string, JsonValue> | null
    ): Promise<void> => {
      if (isRevisionFlow(source)) {
        await rejectWithRevision(source.externalId, buildRevisionToolOutput(source, toolOutput));
        return;
      }
      await execute(source.externalId, toolOutput);
    },
    [execute, rejectWithRevision]
  );

  return useMemo(
    () => ({
      approve,
      reject,
      execute,
      rejectWithRevision,
      finalizeFromSource,
      switchAlternative,
      updatePayload,
    }),
    [
      approve,
      reject,
      execute,
      rejectWithRevision,
      finalizeFromSource,
      switchAlternative,
      updatePayload,
    ]
  );
}
